import { Observable } from 'rxjs';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output
} from '@angular/core';
import { XlatPipe } from '@amt/i18n/xlat-pipe';
import { IOcVehicleSchematicConfig } from '@Interfaces/visualInspection.interfaces';
import { IRendererConfig } from '@Modules/site/conditionMonitoring/monitoringOptions/renderer-config-provider/IRendererConfig';
import { IColumns, IDataConfig } from '@Modules/components/custom-grid/custom-grid.component';
import { TreadWearDetails } from '@Models/condition-monitoring/TreadWearDetails';
import { IPosition } from '@Models/condition-monitoring/IVisualInspection';
import { 
  ConditionMonitoringService,
  ICMEquipmentAlerts,
  ICMTreadWearDetailsDisplay,
  IDisplayVehicle,
  IPositionData,
  ISelectedPosition,
} from '@Modules/site/conditionMonitoring/condition-monitoring.service';
import OcConfigSvc from '@Services/ocConfigSvc';
import {
  ICON_WIDTH,
  ICON_HEIGHT,
} from '../../condition-monitoring-details.component';

type RemainingDepth = { remainingDepth: string, averageDepth: string };

@Component({
  selector: 'thread-wear',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './thread-wear.component.html',
  styleUrls: ['./thread-wear.component.scss'],
  providers: [
    { provide: 'desiredWidth', useValue: ICON_WIDTH },
    { provide: 'desiredHeight', useValue: ICON_HEIGHT },
  ],
})
export class TreadWearDetailsComponent implements OnInit {
  @Input() vehicle!: IDisplayVehicle;
  @Input() currentVehicle$!: Observable<any>;
  @Input() currentVehicle?: any
  @Input() rendererConfig!: IRendererConfig;
  @Output() updateSelectedComponent = new EventEmitter<ISelectedPosition>();
  @Output() finishedProcessing = new EventEmitter<boolean>();
  @Output() sendCurrentVehicle = new EventEmitter<any>();

  // vehicle Schematic settings
  ocVehicleSchematicConfig: IOcVehicleSchematicConfig = {
    maxWidth: 300,
    maxHeight: 350,
    leftPos: 1,
    topAxle: 1
  };
  svgReset?: Function;
  selectedComponent: ISelectedPosition = null;
  private vehicleDetails: any;
  vehicleIssues?: { equipmentId: string, active: boolean, action: string }[];
  details?: TreadWearDetails;

  // details table
  selectedDetails?: ICMTreadWearDetailsDisplay[];
  fitments?: IPosition[];
  dataGridConfig?: IDataConfig;
  columns: IColumns<ICMTreadWearDetailsDisplay>[] = [
      {
        title: this.xlatPipe.transform('visualInspection.lastUpdated'),
        field: 'inspection_date',
        isUrl: true,
      },
      {
        title: this.xlatPipe.transform('UI.Resource.Common.Pos'),
        field: 'posLabel',
      },
      {
        title: this.xlatPipe.transform('visualInspection.cause'),
        field: 'causeLabel',
      },
      {
        title: '% Worn',
        field: 'wearPercentLabel',
        style: (data) => ({
          'value-red': data.action === 'low_tread',
          'value-red-bold': data.action === 'worn_out',
        }),
      },
      {
        title: 'RTD1/RTD2 (Avg)',
        field: 'depth',
        style: (data) => (data.action === 'ASYMMETRICALWEAR' ? {
          'value-red': data.severity === 0,
          'value-red-bold': data.severity === 1,
        } : {}),
      }
    ];

  constructor(
    private monSvc: ConditionMonitoringService,
    private xlatPipe: XlatPipe,
    private cd: ChangeDetectorRef,
    private ocConfigSvc: OcConfigSvc,
  ) {
  }

  ngOnInit(): void {
    if (this.currentVehicle) {
      this.processCurrentVehicle(this.currentVehicle);
    } else {
      this.currentVehicle$.subscribe((x) => this.processCurrentVehicle(x));
    }
  }

  private getJoinedCurrentVehicleIssues() {
    const { vehicleAlerts, vehicleWear } = this.vehicleDetails;
    return [...(vehicleAlerts || []), ...(vehicleWear || [])];
  }

    private processCurrentVehicle(currentVehicle: any) {
      this.doneProcessing();
      this.vehicleDetails = currentVehicle;
      this.fitments = ConditionMonitoringService.getArrPositions(currentVehicle);
      this.prepareDetails(this.getJoinedCurrentVehicleIssues());
      this.sendCurrentVehicle.emit(currentVehicle);
      this.processVehicleIssues();
      this.cd.detectChanges();
    }

  private processVehicleIssues() {
    this.vehicleIssues = this.getJoinedCurrentVehicleIssues().map((a) => {
      const equipmentId = a.equipmentId;
      const action = this.rendererConfig.issues[a.issue] || '';
      return {
        equipmentId,
        active: true,
        action,
      };
    });
    this.cd.detectChanges();
  }

  private doneProcessing() {
    this.finishedProcessing.emit(true);
  }

  // vehicle schematic methods
    tyreSelected(tyre: IPositionData) {
      if (tyre.component.type !== "Tyre" || (!tyre.component.equipmentId || tyre.component.equipmentId.length === 0))
        return; //Should throw an error?

      //If it is the currently selected tyre then unselect it? How does that work with the ocschematic...
      if (this.selectedComponent !== null && this.selectedComponent.equipmentId == tyre.component.equipmentId) {
        this.selectedComponent = null;
      } else {
        this.selectedComponent = tyre.component;

        this.selectedComponent.fittedVehicle = this.vehicle.serialNo;
        this.selectedComponent.percentWorn = ((1 - tyre.component.remainingDepth.value / tyre.component.primaryOTD.value) * 100).toFixed(this.ocConfigSvc.general.percentageWornDecimals).toString() + "%";
        this.selectedComponent.positionLabel = tyre.position;
      }

      this.updateSelectedComponent.emit(this.selectedComponent);
      if (this.vehicleDetails) {
        this.prepareDetails(this.getJoinedCurrentVehicleIssues().filter(i => !this.selectedComponent?.equipmentId || i.equipmentId === this.selectedComponent.equipmentId));
      }
      this.cd.markForCheck()
    }

  showVehicle() {
    this.monSvc.showVehicle(this.vehicle, this.vehicleDetails);
  }

  showSelectedTyre() {
    this.monSvc.showComponent(this.selectedComponent);
  }

  private prepareDetails(issues: ICMEquipmentAlerts[]) {
    const lastTreadReadingDate = this.vehicleDetails.lastTreadReading?.date;
    const rawIssues = issues?.sort((a, b) => (b.inspectionDate?.getTime() || 0) - (a.inspectionDate?.getTime() || 0)) || [];
    const asyWear = rawIssues.filter(i => i.action === 'ASYMMETRICALWEAR');
    const matchingLimit = rawIssues.filter(i => i.action === 'MATCHINGLIMITEXCEEDED');
    const lowTread = rawIssues.filter(i => i.action === 'low_tread');
    const wornOut = rawIssues.filter(i => i.action === 'worn_out');
    this.details = {
      vehicleId: this.vehicle.equipmentId,
      lastTreadReadingDate,
      // asymetric wear alert
      activeAsyWearAlert: asyWear.length,
      lastAsyWearAlert: asyWear[0]?.inspectionDate,
      // matching limit exceeded alert
      activeTreadMatchLimitAlert: matchingLimit.length,
      lastTreadMatchLimitAlert: matchingLimit[0]?.inspectionDate,
      // low tread
      activeLowTread: lowTread.length,
      lastLowTread: lowTread[0]?.inspectionDate,
      // worn out
      activeWornOut: wornOut.length,
      lastWornOut: wornOut[0]?.inspectionDate,
    };
    const severityTreshold = 5; // < 5mm is low severity, >= 5 is high severity
    this.selectedDetails = rawIssues.map(i => {
      const depth: RemainingDepth = i.depth1 && i.depth2
        ? {
          remainingDepth: `${i.depth1}/${i.depth2}`,
          averageDepth: `${ i.averageRemainingDepth ? i.averageRemainingDepth : (parseInt(i.depth1) + parseInt(i.depth2)) / 2}`,
        } 
        : this.calculateDepth(i);
      const wearDifference = i.depth1 && i.depth2
        ? Math.abs(parseInt(i.depth1) - parseInt(i.depth2))
        : depth.remainingDepth
          .split('/')
          .map((num) => parseInt(num))
          .reduce((prev, curr, idx) => idx === 0 ? curr : Math.abs(prev - curr));
      const severity = i.wearValue
        ? (wearDifference < parseInt(i.wearValue) + severityTreshold ? 0 : 1)
        : -1;
      const wearPercent = (1 - parseFloat(depth.averageDepth) / parseFloat(i.originalDepth ?? '1'))  * 100;
        return {
          ...i,
          date: i.inspectionDate, // for inspection_date
          depth: `${depth.remainingDepth} (${depth.averageDepth})`,
          causeLabel: this.translateCause(i.cause, severity),
          wearPercent,
          wearPercentLabel: `${wearPercent.toFixed(2)}%`,
          wearDifference,
          severity,
        }
      }).sort((a, b) => {
        if (!a.posIdx  || !b.posIdx) {
          a.posIdx = a.posIdx || issues.find(i => i.equipmentId === a.equipmentId)?.posIdx || 0;
          b.posIdx = b.posIdx || issues.find(i => i.equipmentId === b.equipmentId)?.posIdx || 0;
        }
        return a.posIdx - b.posIdx || a.issue - b.issue;
      });
    this.cd.detectChanges();
  }
  
  showComponent(event: any) {
    if (this.selectedComponent == null) {
      this.updateSelectedComponent.emit()
    }
    this.monSvc.showComponent(event.dataItem);
  }

  tableHeaderDetails(option?: string): string {
    if (!option) {
      option = this.selectedComponent === null
        ? 'conditionmonitoring.NoTyreDetails'
        : 'conditionmonitoring.TyreDetails'
    }
    return this.selectedComponent === null
      ? this.xlatPipe.transform(option)
      : `${this.xlatPipe.transform(option)} - Pos. ${this.selectedComponent.positionLabel}`;
  }

  private calculateDepth(issue: ICMEquipmentAlerts) : RemainingDepth {
    const fitment = this.fitments?.find(f => f.equipmentId === issue.equipmentId);
    const tyreDetails = this.vehicleDetails?.specification?.axles
      .find((a:any) => a.id === fitment?.axleId)?.positions
      .find((p:any) => p.label === issue.posLabel)?.fitments
      .find((f:any) => f.type === 'Tyre');
    return {
      remainingDepth: tyreDetails.remainingDepth.innerOuterValues,
      averageDepth: `${tyreDetails.remainingDepth.value}`,
    };
  }

  private translateCause(cause: string, severity: number) {
    let text = cause;
    switch (cause) {
      case 'low_tread':
        text = 'tw_low_tread_tyre_note';
          break;
      case 'worn_out':
        text = 'tw_worn_out_tyre_note';
          break;
      case "ASYMMETRICALWEAR":
        switch (severity) {
          case 0: text = 'LowAsymmetricWear';
            break;
          case 1: text = 'HighAsymmetricWear';
            break;
          default: return cause;
        }
        break;
      case "MATCHINGLIMITEXCEEDED":
        text = 'tw_tread_matching_issue';
        break;
      default: return cause;
    }
    return this.xlatPipe.transform(`conditionmonitoring.${text}`);
  } 
}